import React, { useState, useEffect } from 'react';
import { getMerchants, getAdminLedger, getLedgerStats } from '../../services/api';
import { getMerchants as getAdminMerchants } from '../../services/adminApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowUpRight, ArrowDownRight, RefreshCw, Download, Filter, 
  DollarSign, TrendingUp, TrendingDown, Receipt, FileText,
  Plus, Minus
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { formatDateTimeDDMMYYYY } from '../../utils/dateUtils';

const LedgerReports = () => {
  const { admin } = useAdminAuth();
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, totalPages: 0, totalElements: 0 });
  
  // Redirect if not authenticated
  useEffect(() => {
    if (!admin) {
      return;
    }
    loadMerchants();
    loadData();
  }, [admin]);
  
  const [filters, setFilters] = useState({
    merchantId: 'all',
    transactionType: 'all',
    apiCode: '',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  });

  // Commented out - duplicate useEffect causing unnecessary API calls
  // useEffect(() => {
  //   loadMerchants();
  //   loadData();
  // }, []);

  const loadMerchants = async () => {
    try {
      // Use adminApi version which uses 'pageSize' parameter
      const res = await getAdminMerchants(0, 100);
      setMerchants(res.data.content || res.data || []);
    } catch (err) {
      // Set empty array to prevent UI errors
      setMerchants([]);
    }
  };

  const loadData = async (page = 0) => {
    setLoading(true);
    try {
      const params = {
        page,
        size: 20,
        fromDate: filters.fromDate,
        toDate: filters.toDate
      };
      if (filters.merchantId && filters.merchantId !== 'all') params.merchantId = filters.merchantId;
      if (filters.transactionType && filters.transactionType !== 'all') params.transactionType = filters.transactionType;
      if (filters.apiCode) params.apiCode = filters.apiCode;
      
      // Get ledger data first
      const ledgerRes = await getAdminLedger(params);

      // Use real API data with correct field mapping
      const ledgerData = ledgerRes.data?.content || [];
      setLedgerEntries(ledgerData);
      setPagination({ 
        page: ledgerRes.data?.currentPage || 0, 
        totalPages: ledgerRes.data?.totalPages || 1, 
        totalElements: ledgerRes.data?.totalElements || 0 
      });

      // Calculate stats from current page since backend stats API is not required/failing
      const calculatedStats = calculateStats(ledgerData);
      setStats({ ...calculatedStats, isPartial: true });
    } catch (err) {
      console.error('Failed to load admin ledger data:', err);
      toast.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadData(0);
  };

  const calculateStats = (data) => {
    const totalDebit = data.filter(d => d.transactionType === 'DEBIT' || d.transactionType === 'CHARGE_ONLY')
      .reduce((sum, d) => sum + (d.netAmount || 0), 0);
    const totalCommission = data.filter(d => d.transactionType === 'COMMISSION')
      .reduce((sum, d) => sum + (d.netAmount || 0), 0);
    const totalCharges = data.reduce((sum, d) => sum + (d.chargeAmount || 0) + (d.gstAmount || 0), 0);
    const totalTds = data.reduce((sum, d) => sum + (d.tdsAmount || 0), 0);
    
    return { totalDebit, totalCommission, totalCharges, totalTds, transactionCount: data.length };
  };

  const handleClearFilters = () => {
    setFilters({
      merchantId: 'all',
      transactionType: 'all',
      apiCode: '',
      fromDate: new Date().toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0]
    });
  };

  const getAmountColor = (type) => {
    switch(type) {
      case 'CREDIT':
        return 'text-green-600 font-semibold';
      case 'DEBIT':
        return 'text-red-600 font-semibold';
      case 'REFUND':
        return 'text-blue-600 font-semibold';
      case 'COMMISSION':
        return 'text-purple-600 font-semibold';
      case 'CHARGE_ONLY':
        return 'text-orange-600 font-semibold';
      default:
        return 'text-gray-600';
    }
  };

  const getTypeBadge = (type) => {
    const config = {
      'DEBIT': { bg: 'bg-red-100 text-red-800 border-red-300', icon: <Minus className="h-3 w-3 mr-1" /> },
      'CREDIT': { bg: 'bg-green-100 text-green-800 border-green-300', icon: <Plus className="h-3 w-3 mr-1" /> },
      'COMMISSION': { bg: 'bg-purple-100 text-purple-800 border-purple-300', icon: <TrendingUp className="h-3 w-3 mr-1" /> },
      'REFUND': { bg: 'bg-blue-100 text-blue-800 border-blue-300', icon: <RefreshCw className="h-3 w-3 mr-1" /> },
      'CHARGE_ONLY': { bg: 'bg-orange-100 text-orange-800 border-orange-300', icon: <Receipt className="h-3 w-3 mr-1" /> },
      'ADJUSTMENT': { bg: 'bg-gray-100 text-gray-800 border-gray-300', icon: <FileText className="h-3 w-3 mr-1" /> }
    };
    const { bg, icon } = config[type] || config['ADJUSTMENT'];
    return <Badge className={`${bg} flex items-center border`}>{icon}{type}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const exportData = async () => {
    setExporting(true);
    toast.info('Preparing export for all pages...');
    
    try {
      const allEntries = [];
      let currentPage = 0;
      let totalPages = 1;
      
      const params = {
        size: 500, // Fetch larger chunks for export
        fromDate: filters.fromDate,
        toDate: filters.toDate
      };
      if (filters.merchantId && filters.merchantId !== 'all') params.merchantId = filters.merchantId;
      if (filters.transactionType && filters.transactionType !== 'all') params.transactionType = filters.transactionType;
      if (filters.apiCode) params.apiCode = filters.apiCode;

      while (currentPage < totalPages) {
        const res = await getAdminLedger({ ...params, page: currentPage });
        const content = res.data?.content || [];
        allEntries.push(...content);
        totalPages = res.data?.totalPages || 1;
        currentPage++;
        
        // Progress toast update for very large datasets
        if (currentPage % 5 === 0) {
          toast.info(`Fetching data... (${allEntries.length} records)`);
        }
      }

      const headers = [
        'Txn ID', 'Merchant Name', 'Merchant ID', 'Type', 'API', 'Reference ID', 
        'Description', 'Status', 'Base Amount', 'Charge', 'GST', 'Commission', 'TDS', 
        'Net Amount', 'Opening Balance', 'Closing Balance', 'Date'
      ];
      const rows = allEntries.map(e => [
        `"${e.transactionId || ''}"`,
        `"${e.merchant?.merchantName || 'Unknown'}"`,
        `"${e.merchant?.merchantId || 'N/A'}"`,
        `"${e.transactionType || ''}"`,
        `"${e.apiCode || 'N/A'}"`,
        `"${e.referenceId || ''}"`,
        `"${(e.description || '').replace(/"/g, '""')}"`, 
        `"${e.status || ''}"`,
        e.baseAmount || 0,
        e.chargeAmount || 0,
        e.gstAmount || 0,
        e.commissionAmount || 0,
        e.tdsAmount || 0,
        e.netAmount || 0,
        e.openingBalance || 0,
        e.closingBalance || 0,
        `"${formatDateTimeDDMMYYYY(e.createdAt)}"`
      ]);
      
      const csvContent = "\uFEFF" + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledger-report-${filters.fromDate}-${filters.toDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Successfully exported ${allEntries.length} records.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  if (loading && ledgerEntries.length === 0) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Ledger Reports</h1>
        <Button variant="outline" onClick={exportData} disabled={exporting}>
          {exporting ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[180px]">
              <Label>Merchant</Label>
              <Select value={filters.merchantId} onValueChange={(v) => setFilters({...filters, merchantId: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="All Merchants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Merchants</SelectItem>
                  {merchants.map(m => (
                    <SelectItem key={m.id} value={String(m.id)}>{m.merchantName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* <div className="min-w-[150px]">
              <Label>Type</Label>
              <Select value={filters.transactionType} onValueChange={(v) => setFilters({...filters, transactionType: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="DEBIT">Debit</SelectItem>
                  <SelectItem value="CREDIT">Credit</SelectItem>
                  <SelectItem value="COMMISSION">Commission</SelectItem>
                  <SelectItem value="REFUND">Refund</SelectItem>
                  <SelectItem value="CHARGE_ONLY">Charge Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px]">
              <Label>API Code</Label>
              <Input 
                placeholder="Filter by API"
                value={filters.apiCode}
                onChange={(e) => setFilters({...filters, apiCode: e.target.value})}
              />
            </div> */}
            <div>
              <Label>From Date</Label>
              <Input type="date" value={filters.fromDate} onChange={(e) => setFilters({...filters, fromDate: e.target.value})} />
            </div>
            <div>
              <Label>To Date</Label>
              <Input type="date" value={filters.toDate} onChange={(e) => setFilters({...filters, toDate: e.target.value})} />
            </div>
            <Button onClick={handleFilter}><Filter className="h-4 w-4 mr-2" /> Apply</Button>
            <Button variant="outline" onClick={handleClearFilters}>Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Ledger Table */}
      <Card>
        <CardHeader>
          <CardTitle>Ledger Entries ({pagination.totalElements})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Reference ID</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Base Amount</TableHead>
                <TableHead className="text-right">Charge + GST</TableHead>
                <TableHead className="text-right">Commission - TDS</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead className="text-right">Opening Balance</TableHead>
                <TableHead className="text-right">Closing Balance</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledgerEntries.map((entry) => (
                <TableRow 
                  key={entry.id} 
                  className="cursor-pointer hover:bg-gray-50 text-sm"
                  onClick={() => setSelectedEntry(entry)}
                >
                  <TableCell className="font-mono text-xs">{entry.referenceId || '-'}</TableCell>
                  <TableCell className="max-w-xs truncate" title={entry.description}>
                    {entry.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className={`bg-green-100 text-green-700 border-green-300`}>
                      {entry.status || 'Unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {entry.baseAmount > 0 ? formatCurrency(entry.baseAmount) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {(entry.chargeAmount > 0 || entry.gstAmount > 0) ? (
                      <div className="flex flex-col">
                        <span className="text-orange-600">{formatCurrency(entry.chargeAmount)}</span>
                        <span className="text-gray-400 text-[10px]">+ {formatCurrency(entry.gstAmount)}</span>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {(entry.commissionAmount > 0 || entry.tdsAmount > 0) ? (
                      <div className="flex flex-col">
                        <span className="text-green-600">{formatCurrency(entry.commissionAmount)}</span>
                        <span className="text-red-400 text-[10px]">- {formatCurrency(entry.tdsAmount)}</span>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${getAmountColor(entry.transactionType)}`}>
                    {entry.transactionType === 'COMMISSION' || entry.transactionType === 'CREDIT' || entry.transactionType === 'REFUND' ? '+' : '-'}
                    {formatCurrency(entry.netAmount)}
                  </TableCell>
                  <TableCell className="text-right text-gray-500">
                    {formatCurrency(entry.openingBalance)}
                  </TableCell>
                  <TableCell className="text-right font-medium text-blue-600">
                    {formatCurrency(entry.closingBalance)}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDateTimeDDMMYYYY(entry.createdAt)}</TableCell>
                </TableRow>
              ))}
              {ledgerEntries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-8 text-gray-500">
                    No ledger entries found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {pagination.totalPages > 0 && (
        <div className="flex justify-between items-center mt-4 px-4 pb-4">
          <div className="text-sm text-gray-600">
            {pagination.page * 20 + 1}-{Math.min((pagination.page + 1) * 20, pagination.totalElements)} of {pagination.totalElements}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadData(pagination.page - 1)}
              disabled={pagination.page === 0 || loading}
            >
              Previous
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => {
              const p = i;
              const cur = pagination.page;
              const tot = pagination.totalPages;
              
              if (tot <= 7 || p === 0 || p === tot - 1 || (p >= cur - 1 && p <= cur + 1)) {
                return (
                  <Button
                    key={i}
                    variant={i === cur ? 'default' : 'outline'}
                    size="sm"
                    className="w-8 h-8 p-0"
                    onClick={() => loadData(i)}
                  >
                    {i + 1}
                  </Button>
                );
              }
              if (p === 1 && cur > 2) return <span key={i} className="px-1 text-gray-400">...</span>;
              if (p === tot - 2 && cur < tot - 3) return <span key={i} className="px-1 text-gray-400">...</span>;
              return null;
            })}
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadData(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages - 1 || loading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Entry Detail Dialog */}
      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ledger Entry Details</DialogTitle>
          </DialogHeader>
          {selectedEntry && (
            <Tabs defaultValue="overview">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="charges">Charges & Commission</TabsTrigger>
                <TabsTrigger value="balance">Balance</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <InfoItem label="Transaction ID" value={selectedEntry.transactionId} mono />
                  <InfoItem label="Type" value={getTypeBadge(selectedEntry.transactionType)} />
                  <InfoItem label="Merchant" value={selectedEntry.merchant?.merchantName || 'Unknown'} />
                  <InfoItem label="Merchant ID" value={selectedEntry.merchant?.merchantId || 'N/A'} mono />
                  <InfoItem label="API Code" value={<Badge variant="outline">{selectedEntry.apiCode || 'N/A'}</Badge>} />
                  <InfoItem label="Reference ID" value={selectedEntry.referenceId || 'N/A'} mono />
                  <InfoItem label="Description" value={selectedEntry.description} className="col-span-2" />
                  <InfoItem label="Status" value={<Badge className="bg-green-100 text-green-700">{selectedEntry.status || 'Unknown'}</Badge>} />
                  <InfoItem label="Date" value={formatDateTimeDDMMYYYY(selectedEntry.createdAt)} />
                </div>
                {selectedEntry.slabRange && (
                  <div className="p-3 bg-purple-50 rounded border border-purple-200">
                    <span className="text-purple-600 text-sm">Slab Applied: </span>
                    <span className="font-medium">{selectedEntry.slabRange}</span>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="charges" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium mb-3 flex items-center">
                      <Receipt className="h-4 w-4 mr-2 text-orange-500" /> Charges
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Base Amount:</span>
                        <span className="font-medium">{formatCurrency(selectedEntry.baseAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Service Charge:</span>
                        <span className="font-medium text-orange-600">{formatCurrency(selectedEntry.chargeAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">GST ({selectedEntry.gstPercentage}%):</span>
                        <span className="font-medium text-orange-600">{formatCurrency(selectedEntry.gstAmount)}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between font-bold">
                        <span>Total Charges:</span>
                        <span className="text-orange-600">{formatCurrency(selectedEntry.chargeAmount + selectedEntry.gstAmount)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium mb-3 flex items-center">
                      <TrendingUp className="h-4 w-4 mr-2 text-green-500" /> Commission
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Gross Commission:</span>
                        <span className="font-medium text-green-600">{formatCurrency(selectedEntry.commissionAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">TDS ({selectedEntry.tdsPercentage || 0}%):</span>
                        <span className="font-medium text-red-600">-{formatCurrency(selectedEntry.tdsAmount)}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between font-bold">
                        <span>Net Commission:</span>
                        <span className="text-green-600">{formatCurrency(selectedEntry.commissionAmount - selectedEntry.tdsAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Net Amount:</span>
                    <span className={`text-2xl font-bold ${selectedEntry.transactionType === 'COMMISSION' || selectedEntry.transactionType === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedEntry.transactionType === 'COMMISSION' || selectedEntry.transactionType === 'CREDIT' ? '+' : '-'}
                      {formatCurrency(selectedEntry.netAmount)}
                    </span>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="balance" className="space-y-4 mt-4">
                <div className="flex items-center justify-center gap-4 p-6 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Opening Balance</p>
                    <p className="text-2xl font-bold">{formatCurrency(selectedEntry.openingBalance)}</p>
                  </div>
                  <ArrowDownRight className={`h-8 w-8 ${selectedEntry.transactionType === 'COMMISSION' || selectedEntry.transactionType === 'CREDIT' ? 'text-green-500 rotate-180' : 'text-red-500'}`} />
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <p className="text-sm text-gray-500">Transaction</p>
                    <p className={`text-xl font-bold ${selectedEntry.transactionType === 'COMMISSION' || selectedEntry.transactionType === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedEntry.transactionType === 'COMMISSION' || selectedEntry.transactionType === 'CREDIT' ? '+' : '-'}
                      {formatCurrency(selectedEntry.netAmount)}
                    </p>
                  </div>
                  <ArrowDownRight className={`h-8 w-8 ${selectedEntry.transactionType === 'COMMISSION' || selectedEntry.transactionType === 'CREDIT' ? 'text-green-500 rotate-180' : 'text-red-500'}`} />
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Closing Balance</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(selectedEntry.closingBalance)}</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoItem = ({ label, value, className = '', mono = false }) => (
  <div className={`p-3 bg-gray-50 rounded ${className}`}>
    <span className="text-gray-500 text-xs block">{label}</span>
    <span className={`font-medium ${mono ? 'font-mono text-sm' : ''}`}>{value || '-'}</span>
  </div>
);

export default LedgerReports;
